import type { ReceiptQrConfig } from '@delivery/shared';

export type OrderStatus =
  | 'DRAFT' | 'PENDING' | 'ASSIGNED' | 'ACCEPTED' | 'PICKED_UP'
  | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED' | 'RETURNED' | 'CANCELLED';

export interface OrderRow {
  /** SELF | THIRD_PARTY —— 已交给三方的单不能再派自己的司机 */
  dispatchMode?: string | null;
  channelOrderNo?: string | null;
  id: string;
  orderNo: string;
  trackingCode: string;
  externalRef: string | null;
  status: OrderStatus;
  subStatus: string | null;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  receiverLat: number | null;
  receiverLng: number | null;
  packageCount: number;
  weightGram: number | null;
  codAmount: string;
  /** 要代收但金额待定 —— 显示「联系商家确认」,不是 0 */
  codPending?: boolean;
  /** 小票打出来的时间。null = 还没打过 */
  receiptPrintedAt?: string | null;
  /* 三种备注,小票上都要印 —— 人写在哪一个里取决于他从哪个入口建的单 */
  /** 收件备注 —— 跟着地址印 */
  receiverNote?: string | null;
  /** 物品 / 备注 —— 建单弹窗里那个框 */
  itemDescription?: string | null;
  /** 内部备注 —— 导入表格里那一列「备注」 */
  remark?: string | null;
  /** 冻品、易碎…… 分类是每家自己在字典里加的 */
  specialItems?: Array<{ code: string; label: string; icon: string | null }>;
  currency: string;
  customerPrice?: string;
  // 没有 order:view_payout 的角色(比如客服)拿不到这一项
  driverPayout?: string;
  /** 运费怎么算出来的 —— 客服要能一行一行念给客户听 */
  priceBreakdown?: {
    lines: Array<{ code: string; label: string; amount: number }>;
    deliverySubtotal: number;
    codFee: number;
    tax: number;
    remoteToDriver: number;
  } | null;
  // 关联对象的 id 也是 ULID(和详情接口一致,见 dto-helpers.ts)
  zone: { id: string; code: string; name: string } | null;
  driver: { id: string; name: string; phone: string } | null;
  pickupPoint: { id: string; name: string } | null;
  /**
   * printAlias:小票上印的那个名字(商家资料里配的),空 = 不印。
   * receiptQr:小票上印的二维码,空 = 不印。
   */
  customer: {
    id: string;
    name: string;
    printAlias?: string | null;
    receiptQr?: ReceiptQrConfig | null;
  } | null;
  needsReview: boolean;
  expectedDeliveryAt: string | null;
  /**
   * 司机智能推荐路线里排第几(1 起)。null = 没规划过。
   *
   * ★ 调度要能回答客户那句「他什么时候到我这儿」—— 只有司机端看得到的话,
   *   客服每次都得打电话问司机,而他正在开车。
   */
  routeSeq?: number | null;
  /** 预计几点到。**已经含了每一单的停留时间**(默认 5 分钟) */
  routeEtaAt?: string | null;
  createdAt: string;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  version: number;
}

export interface ListResponse {
  data: OrderRow[];
  meta: { cursor: { next: string | null; hasMore: boolean }; total?: number };
}

export interface DriverRow {
  id: string;
  driverNo: string;
  name: string;
  phone: string;
  driverType: string;
  vehicleType: string | null;
  plateNo: string | null;
  status: string;
  dutyStatus: string;
  currentOrderCount: number;
  creditScore: number;
  docsVerified: boolean;
  lastLocatedAt: string | null;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  /** 今日战况。按完成时间统计,不是 updatedAt(见 admin.service) */
  today?: { delivered: number; failed: number; returned: number };
  /** 近 7 天派单后超时未接的次数(数的是 assignment,不是计数器)*/
  timeout7d?: number;
}

export interface PickupPointRow {
  id: string;
  code: string;
  name: string;
  type: string;
  address: string;
  /** 门牌/楼层这类补充 —— 表单要能回填,否则一保存就被清空 */
  addressDetail: string | null;
  lat: number | null;
  lng: number | null;
  contactName: string | null;
  contactPhone: string | null;
  cutoffTime: string | null;
  pickupInstruction: string | null;
  parkingNote: string | null;
  /** 要不要到店才能确认取货。★ 表单必须回填它,不能默认 true */
  requireGeofence: boolean;
  geofenceRadiusM: number;
  /** 只有能派单的人拿得到 —— 对司机是业务量情报,不是取货信息 */
  pendingOrders?: number;
  isActive: boolean;
  /** 这个取货点挂在哪家名下 —— 建单时用它把商家带出来 */
  customer: { publicId: string; name: string } | null;
  zone: { publicId: string; code: string; name: string } | null;
}

export interface ZoneRow {
  id: string;
  code: string;
  name: string;
  priority: number;
  isRemote: boolean;
  remoteSurcharge: string;
  activeOrders: number;
  isActive: boolean;
  defaultDriver: { publicId: string; name: string } | null;
  parent: { id: string; name: string } | null;
}

export interface Me {
  user: { publicId: string; name: string; kind: string };
  tenant: {
    name: string;
    timezone: string;
    currency: string;
    mapProvider: string;
    /** 功能开关的原始值。判断用 shared 的 isFeatureOn —— 它会回落到注册表默认值 */
    features?: Record<string, boolean>;
    /** 地理编码接口地址。为空表示没启用 —— 没坐标的单会进复核队列 */
    geocodeUrl?: string | null;
    /** 密钥的**引用**(env:NAME),不是密钥本身 */
    geocodeKeyRef?: string | null;
    /** 底图:后台配的原始值(设置页要显示) */
    mapTileUrl?: string | null;
    mapKeyRef?: string | null;
    mapAttribution?: string | null;
    /** 底图:服务端已把 {key} 替换好的最终地址。null 表示回落到构建期变量 */
    tiles?: { tileUrl: string | null; attribution: string | null; keyMissing: boolean } | null;
  };
  permissions: string[];
  dataScope: { type: string };
}

/** 状态展示。颜色语义和司机端保持一致 */
export const STATUS_META: Record<OrderStatus, { label: string; cls: string }> = {
  DRAFT: { label: '草稿', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  PENDING: { label: '待派单', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  ASSIGNED: { label: '待接单', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  ACCEPTED: { label: '待取货', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  PICKED_UP: { label: '配送中', cls: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
  IN_TRANSIT: { label: '配送中', cls: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
  DELIVERED: { label: '已送达', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  FAILED: { label: '未送达', cls: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' },
  RETURNED: { label: '已退回', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' },
  CANCELLED: { label: '已取消', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500' },
};

/** 订单事件 —— 时间轴的原始数据 */
export interface OrderEvent {
  seq: number;
  type: string;
  fromStatus: OrderStatus | null;
  toStatus: OrderStatus | null;
  actorType: 'USER' | 'DRIVER' | 'SYSTEM' | 'THIRD_PARTY';
  actorName: string | null;
  lat: number | null;
  lng: number | null;
  /** 司机在离目标多远的地方操作的。远距离签收是核查重点 */
  distanceToTargetM: number | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface OrderProof {
  type: string;
  storageKey: string;
  thumbnailKey: string | null;
  mime: string | null;
  lat: number | null;
  lng: number | null;
  takenAt: string | null;
  createdAt: string;
}

export interface OrderDetail extends OrderRow {
  source: string;
  /** 区是怎么定的:MANUAL / CONTACT(收件人记住的)/ POLYGON(画的范围)/ KEYWORD */
  zoneMatchType?: string | null;
  /** 判区打架时的候选(普通区和偏远区同时命中),复核时点选 */
  zoneCandidates?: Array<{ zoneId: string; keyword: string; code?: string; name?: string; isRemote?: boolean }>;
  priority: number;
  pickupContact: string | null;
  pickupPhone: string | null;
  pickupAddress: string;
  pickupLat: number | null;
  pickupLng: number | null;
  pickupNote: string | null;
  receiverPostcode: string | null;
  receiverNote: string | null;
  /** 从联系人快照过来的常驻派送备注 —— 和上面那条并列,不合成一段 */
  /** 司机自己加的备注 —— 显示在订单备注的上一行 */
  driverNote?: string | null;
  itemDescription: string | null;
  remark: string | null;
  signedByType: string | null;
  signedByName: string | null;
  goodsDisposition: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
  updatedAt: string;
  assignedAt: string | null;
  acceptedAt: string | null;
  events: OrderEvent[];
  proofs: OrderProof[];
}
